/**
 * API Client Utility
 * 
 * Provides centralized API request handling with automatic
 * session expiration detection and cleanup
 */

import { getSupabaseConfig } from './supabase/config';

// Detect if running locally
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Get base URL dynamically based on environment
const getBaseUrl = () => {
  if (isLocal) {
    return 'http://127.0.0.1:54321/functions/v1';
  }
  const config = getSupabaseConfig();
  return `${config.url}/functions/v1`;
};

/**
 * Get the API base URL (automatically detects local vs production)
 */
export function getApiUrl(endpoint: string = ''): string {
  if (endpoint.startsWith('http')) {
    return endpoint;
  }
  return `${getBaseUrl()}/make-server-e90c59cd${endpoint}`;
}

export interface ApiResponse<T = any> {
  ok: boolean;
  data?: T;
  error?: string;
  status: number;
  sessionExpired?: boolean;
}

/**
 * Make an API request with automatic session expiration handling
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const token = localStorage.getItem('pathkit_access_token');
    
    // Add auth header if token exists
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // Cast to any to avoid TS index signature issues with RequestInit headers
      ...(options.headers as any || {}),
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const url = getApiUrl(endpoint);
    
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      console.warn('⚠️ Session expired - clearing authentication');
      
      // Clear local storage
      localStorage.removeItem('pathkit_access_token');
      localStorage.removeItem('pathkit_user');
      localStorage.removeItem('pathkit_company');
      
      // Parse error message
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { error: 'Session expired' };
      }
      
      return {
        ok: false,
        error: errorData.error || 'Session expired. Please sign in again.',
        status: 401,
        sessionExpired: true,
      };
    }
    
    // Handle other errors
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { error: `Request failed with status ${response.status}` };
      }
      
      return {
        ok: false,
        error: errorData.error || `Request failed with status ${response.status}`,
        status: response.status,
      };
    }
    
    // Success - parse response
    const data = await response.json();
    
    return {
      ok: true,
      data,
      status: response.status,
    };
  } catch (error) {
    console.error('API request error:', error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Network error',
      status: 0,
    };
  }
}

/**
 * Handle API response and automatically redirect on session expiration
 */
export function handleApiResponse<T>(
  response: ApiResponse<T>,
  options: {
    onSuccess?: (data: T) => void;
    onError?: (error: string) => void;
    onSessionExpired?: () => void;
  } = {}
): void {
  if (response.sessionExpired) {
    console.log('🔒 Session expired - redirecting to login');
    if (options.onSessionExpired) {
      options.onSessionExpired();
    } else {
      // Default: redirect to login
      window.location.href = '/';
    }
    return;
  }
  
  if (response.ok && response.data) {
    options.onSuccess?.(response.data);
  } else {
    console.error('API error:', response.error);
    options.onError?.(response.error || 'Unknown error');
  }
}
